import React, { useState, useEffect, useCallback } from 'react';
import DocumentViewModal from '../DocumentView/DocumentViewModal';
import TransferReceiptModal from '../Search/ModalTemplates/TransferReceiptModal';
import MoreActionsMenu from '../MoreActionsMenu';
import FileSummaryModal from '../FileSummaryModal';
import './SearchResultsTable.css';
import ReprocessDocumentModal from '../Documents/ReprocessDocumentModal';
import ReprocessInPlaceModal from '../Documents/ReprocessInPlaceModal';
import { useApi } from '../../hooks/useApi';
import { getIdTokenClaims } from '../../common/auth';
import { formatDocumentTypeLabel } from '../../utils/documentTypes';
import { Snackbar, Alert } from '@mui/material';
import Spinner from '../../components/Spinner';
import { getActiveProcessingLabel, isProcessing, isReprocessingStatus } from '../../common/processingStates';
import {
  getReprocessUnavailableReason,
  isReprocessableDocument,
  loadReprocessContext,
} from '../../utils/reprocess';
import {
  inPlaceReprocessToastMessage,
  isDocumentReprocessing,
  isInPlaceReprocessable,
  runInPlaceReprocess,
  useReprocessPending,
} from '../../utils/documentReprocess';
import { fetchCanonicalDocumentRecord } from '../../utils/documentRedirect';
import { buildDocumentByIdQuery } from '../../utils/documentDetailQuery';
import { resolveBedrockMetadata } from '../../utils/bedrockNormalize';
import { formatComprobanteLabel } from '../../utils/fiscalComprobante';
import { resolveArcaDisplay } from '../../utils/arcaDisplay';
import { useArcaFeature } from '../../hooks/useArcaFeature';
import { constatarDocumentArca } from '../../common/apiClient';

const REPROCESS_DOCUMENT_AS = `
  mutation ReprocessDocumentAs($input: ReprocessDocumentAsInput!) {
    reprocessDocumentAs(input: $input) {
      source_document_id
      redirect_target_doc_type
      redirect_target_document_id
      status
      message
    }
  }
`;

// Sample data with mixed invoices and receipts
const sampleMixedData = [
  // Invoices
  {
    id: 1,
    fileName: 'INV-2024-001.pdf',
    documentType: 'invoice',
    fileType: 'pdf',
    customer: 'Olivia Bennett',
    date: '2024-01-15',
    amount: '\$4,080.00',
    status: 'Processed'
  },
  {
    id: 2,
    fileName: 'Invoice-ABC-789.pdf',
    documentType: 'invoice',
    fileType: 'pdf',
    customer: 'Tech Solutions Ltd',
    date: '2024-01-14',
    amount: '\$12,500.00',
    status: 'Processing'
  },
  {
    id: 3,
    fileName: 'Factura-2024-0023.pdf',
    documentType: 'invoice',
    fileType: 'pdf',
    customer: 'Global Services Inc',
    date: '2024-01-13',
    amount: '\$8,750.00',
    status: 'Validating'
  },

  // Receipts
  {
    id: 4,
    fileName: 'Receipt-Transfer-001.pdf',
    documentType: 'receipt',
    fileType: 'pdf',
    customer: 'Mateo Vargas',
    sender: 'Sofia Herrera',
    date: '2024-01-15',
    amount: '\$5,000.00',
    status: 'Processed',
    transactionId: '1234567890',
    concept: 'Payment for Services'
  },
  {
    id: 5,
    fileName: 'REC-Bank-Transfer-456.pdf',
    documentType: 'receipt',
    fileType: 'pdf',
    customer: 'Digital Agency Co',
    sender: 'Marketing Pro LLC',
    date: '2024-01-14',
    amount: '\$3,200.00',
    status: 'Processed',
    transactionId: '9876543210',
    concept: 'Monthly Retainer'
  },
  {
    id: 6,
    fileName: 'Recibo-Pago-789.pdf',
    documentType: 'receipt',
    fileType: 'pdf',
    customer: 'Ana Rodriguez',
    sender: 'Carlos Martinez',
    date: '2024-01-13',
    amount: '\$1,500.00',
    status: 'Processing',
    transactionId: '5432167890',
    concept: 'Consulting Fee'
  },

  // More Invoices
  {
    id: 7,
    fileName: 'INV-2024-002.pdf',
    documentType: 'invoice',
    fileType: 'pdf',
    customer: 'Software Dynamics',
    date: '2024-01-12',
    amount: '\$15,000.00',
    status: 'Processed'
  },
  {
    id: 8,
    fileName: 'Invoice-XYZ-123.pdf',
    documentType: 'invoice',
    fileType: 'pdf',
    customer: 'Creative Studios',
    date: '2024-01-11',
    amount: '\$6,800.00',
    status: 'Failed'
  },

  // More Receipts
  {
    id: 9,
    fileName: 'Transfer-Receipt-2024.pdf',
    documentType: 'receipt',
    fileType: 'pdf',
    customer: 'Innovation Hub',
    sender: 'Tech Ventures',
    date: '2024-01-11',
    amount: '\$10,000.00',
    status: 'Validating',
    transactionId: '1122334455',
    concept: 'Investment Payment'
  },
  {
    id: 10,
    fileName: 'Bank-Receipt-567.pdf',
    documentType: 'receipt',
    fileType: 'pdf',
    customer: 'Startup Inc',
    sender: 'Venture Capital Fund',
    date: '2024-01-10',
    amount: '\$25,000.00',
    status: 'Processed',
    transactionId: '6677889900',
    concept: 'Seed Funding'
  },

  // Unclassified documents
  {
    id: 11,
    fileName: 'Contract-2024.docx',
    documentType: 'unclassified',
    fileType: 'word',
    customer: 'John Doe',
    date: '2024-01-14',
    amount: '-',
    status: 'Processing'
  },
  {
    id: 12,
    fileName: 'Report-Q1-2024.xlsx',
    documentType: 'unclassified',
    fileType: 'excel',
    customer: 'Finance Dept',
    date: '2024-01-13',
    amount: '-',
    status: 'Not Validated'
  }
];

const SearchResultsTable = ({
  data = sampleMixedData, // Use sample data as default
  title = "Processing documents",
  // Optional callbacks for actions
  onViewMore,
  onDownload,
  onRename,
  onTryAgain,
  onDelete,
  onConciliate, // New callback for receipt conciliation
  onRefresh,
  onReprocessStarted,
  // Optional API endpoint for fetching summaries
  summaryApiEndpoint = null,
  // Document type filter
  documentTypeFilter = 'all' // 'all', 'invoices', 'receipts', 'unclassified'
}) => {
  useReprocessPending();

  const [fichaModal, setFichaModal] = useState({ open: false, record: null });

  // Transfer Receipt Modal states
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);

  // File Summary Modal states
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [summaryData, setSummaryData] = useState({
    fileName: '',
    summary: '',
    fullContent: '',
    loading: false,
    error: null
  });

  const [, setResults] = useState([]);
  const { callApi } = useApi();
  const [reprocessDocTypes, setReprocessDocTypes] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const { enabled: arcaEnabled, canConstatar } = useArcaFeature();
  const [arcaLoadingId, setArcaLoadingId] = useState(null);
  const [reprocessModal, setReprocessModal] = useState(null);
  const [inPlaceModal, setInPlaceModal] = useState(null);
  const [reprocessLoading, setReprocessLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const refreshReprocessContext = useCallback(async () => {
    const { canEdit: editable, reprocessDocTypes: types } = await loadReprocessContext(
      callApi,
      getIdTokenClaims
    );
    setCanEdit(editable);
    setReprocessDocTypes(types);
    return { canEdit: editable, reprocessDocTypes: types };
  }, [callApi]);

  useEffect(() => {
    let cancelled = false;

    refreshReprocessContext().catch((err) => {
      if (!cancelled) {
        console.warn('Failed to load reprocess context:', err);
      }
    });

    return () => { cancelled = true; };
  }, [refreshReprocessContext]);

  const handleReprocessMenuOpen = useCallback(async (row) => {
    const docType = (row?.doc_type || row?.type || '').toLowerCase();
    if (docType !== 'unclassified') {
      return;
    }

    const ctx = await refreshReprocessContext();
    if (isReprocessableDocument(row, ctx)) {
      return;
    }

    const reason = getReprocessUnavailableReason({
      row,
      canEdit: ctx.canEdit,
      reprocessDocTypes: ctx.reprocessDocTypes,
    });
    if (reason) {
      setToast({ open: true, message: reason, severity: 'warning' });
    }
  }, [refreshReprocessContext]);
  // Mock invoice data
  const mockInvoiceData = {
    tipo_documento: "Factura",
    letra_documento: "A",
    punto_de_venta: "0001",
    fecha_emision: "2024-07-26",
    razon_social_emisor: "Invoicy Inc.",
    cuit_emisor: "30-12345678-9",
    razon_social_receptor: "Olivia Bennett",
    cuit_receptor: "27-98765432-1",
    numero_documento: "INV-2024-001",
    monto_total: 4080.00,
    moneda: "USD",
    subtotal: 3000.00,
    impuesto: 0,
    "iva 21%": 630.00,
    "iva 10.5%": 0,
    CAE: "74123456789012",
    condiciones_de_venta: "Contado",
    orden_de_compra: "OC-2024-456",
    remito: "R-2024-789",
    codigo_factura: "A-0001-00000123",

    items: [
      {
        nombre: "Consulting Services",
        cantidad: 10,
        precio_unitario: 100.00,
        codigo: "CS001",
        importe: 1000.00,
        subtotal: 1000.00,
        subitems: []
      },
      {
        nombre: "Project Management",
        cantidad: 5,
        precio_unitario: 200.00,
        codigo: "PM001",
        importe: 1000.00,
        subtotal: 1000.00,
        subitems: [
          {
            tipo: "Nota de Pedido",
            referencia: "NP-2024-123",
            detalle: "Gestión proyecto web",
            precio: null
          }
        ]
      },
      {
        nombre: "Web Development",
        cantidad: 20,
        precio_unitario: 50.00,
        codigo: "WD001",
        importe: 1000.00,
        subtotal: 1000.00,
        subitems: []
      }
    ],
    retenciones: [
      {
        nombre: "IIBB",
        descripcion: "15%",
        importe: 450.00
      }
    ],
    tags: [
      { "cuit_emisor": "30-12345678-9" },
      { "razon_social_emisor": "Invoicy Inc." },
      { "moneda": "USD" },
      { "tipo_documento": "Factura" },
      { "letra_documento": "A" },
      { "categoria_emisor": "Servicios" },
      { "fecha_emision": "2024-07-26" }
    ]
  };

  // Mock receipt data
  const mockReceiptData = {
    transactionId: '1234567890',
    date: '2024-01-15',
    amount: '\$5,000.00 USD',
    origin: {
      accountHolder: 'Sofia Herrera',
      bank: 'Banco Nacional',
      accountNumber: '123-456-7890',
      cuit: '20-12345678-9'
    },
    destination: {
      accountHolder: 'Mateo Vargas',
      bank: 'Banco Federal',
      accountNumber: '987-654-3210',
      cuit: '27-98765432-1'
    },
    concept: 'Pago por Servicios',
    status: 'Completado',
    tags: 'Factura #1234'
  };

  const mockUnclassified = {
    "tipo_documento": "other",
    "categoria_documento": "Curriculum Vitae",
    "title": "Victor M. Iglesias - Chief Technology Officer (CTO)",
    "summary": "Victor M. Iglesias es un experimentado Chief Technology Officer (CTO) con más de 15 años de experiencia en banca, seguros y startups tecnológicas. Experto en transformar estrategias de negocio en soluciones tecnológicas escalables, acompañando procesos de digitalización y crecimiento organizacional. Tiene amplia experiencia en construir y escalar equipos de ingeniería, definir roadmaps de innovación, gestionar presupuestos multimillonarios y participar en rondas de inversión. Su enfoque está en asegurar que la tecnología genere impacto en la eficiencia, escalabilidad y rentabilidad del negocio."
  }

  // Determine document type based on filename or metadata
  const getDocumentType = (row) => {
    const fileName = row.filename?.toLowerCase() || '';
    const fileType = row.doc_type?.toLowerCase() || '';
    const documentType = row.doc_type?.toLowerCase() || '';

    const isInvoiceDocType = (value) => {
      const normalized = (value || '').toLowerCase();
      return normalized === 'invoices'
        || normalized === 'invoice'
        || normalized.includes('inv-');
    };

    if (isInvoiceDocType(fileName) || isInvoiceDocType(fileType) || isInvoiceDocType(documentType)) {
      return 'invoice';
    }

    if (fileType.includes('receipts') ||
      fileType === 'receipts' ||
      documentType === 'receipts') {
      return 'receipt';
    }

    // Default to unclassified
    return fileType;
  };

  // Filter data based on document type and active tab
  const getFilteredData = () => {

    let filteredByType = data;

    // Filter by document type
    if (documentTypeFilter !== 'all') {
      filteredByType = data.filter(row => {
        const docType = getDocumentType(row);
        if (documentTypeFilter === 'invoices' && docType === 'invoice') return true;
        if (documentTypeFilter === 'receipts' && docType === 'receipt') return true;
        if (documentTypeFilter === 'unclassified' && docType === 'unclassified') return true;
        return false;
      });
    }

    return filteredByType
  };

  const handleFileClick = async (row) => {
    const docType = getDocumentType(row);

    const openFicha = async () => {
      try {
        const apiData = await fetchCanonicalDocumentRecord(
          callApi,
          buildDocumentByIdQuery,
          row.document_id,
        );
        setFichaModal({ open: true, record: apiData });
      } catch (err) {
        console.error('Error fetching document:', err);
        setResults([]);
      }
    };

    if (docType === 'invoice') {
      await openFicha();
      return;
    }
    if (docType === 'receipt') {
      //  Handle receipt click
      try {
        const data = await callApi(buildDocumentByIdQuery(row.document_id));
        const apiData = data.getDocumentById;
        const bedrock = resolveBedrockMetadata(apiData);
        const receiptData = {
          ...apiData,
          transactionId: row.transactionId || bedrock.identificador_transaccion,
          date: row.date || bedrock.fecha_transferencia,
          amount: row.amount || bedrock.importe_transferido,
          origin: {
            accountHolder: bedrock.titular_origen,
            bank: bedrock.entidad_origen,
            accountNumber: bedrock.cuenta_origen,
            cuit: bedrock.cuit_origen,
          },
          destination: {
            accountHolder: bedrock.titular_destino,
            bank: bedrock.entidad_destino,
            accountNumber: bedrock.cuenta_destino,
            cuit: bedrock.cuit_destino,
          },
          tags: (bedrock.tags || [])
            .filter(t => ["moneda", "concepto", "estado"].includes(t.nombre))
            .map(t => t.valor ?? ""),
          concept: bedrock.concepto,
          status: bedrock.estado === 'Processed' ? 'Completado' :
            bedrock.estado === 'Processing' ? 'Procesando' :
              bedrock.estado === 'Failed' ? 'Cancelado' : 'Pendiente'
        };

        setSelectedReceipt(receiptData);
        setIsReceiptModalOpen(true);
      }
      catch (err) {
        console.error("Error fetching invoice:", err);
        setResults([])
      }
    }
    if (docType !== 'invoice' && docType !== 'receipt') {
      await openFicha();
    }
  };

  const closeFichaModal = () => {
    setFichaModal({ open: false, record: null });
  };

  const handleFichaClassify = async (targetDocType) => {
    const docId = fichaModal.record?.document_id;
    if (!docId || !targetDocType) return;
    setReprocessLoading(true);
    try {
      const data = await callApi(REPROCESS_DOCUMENT_AS, {
        input: { document_id: docId, target_doc_type: targetDocType },
      });
      const result = data?.reprocessDocumentAs;
      setToast({
        open: true,
        message: result?.message || `Reproceso iniciado como ${formatDocumentTypeLabel(targetDocType)}`,
        severity: result?.status === 'QUEUED' ? 'success' : 'info',
      });
      closeFichaModal();
      onReprocessStarted?.(result);
    } catch (err) {
      setToast({ open: true, message: err.message || 'Error al clasificar', severity: 'error' });
    } finally {
      setReprocessLoading(false);
    }
  };

  const closeReceiptModal = () => {
    setIsReceiptModalOpen(false);
    setSelectedReceipt(null);
  };

  const handleConciliate = async (receiptData) => {
    console.log('Conciliating receipt:', receiptData);
    if (onConciliate) {
      await onConciliate(receiptData);
    }
  };

  const openReprocessModal = useCallback((row) => {
    if (!row) return;
    setReprocessModal({
      document_id: row.document_id || row.id,
      filename: row.filename || row.fileName,
    });
  }, []);

  const closeReprocessModal = () => {
    if (!reprocessLoading) {
      setReprocessModal(null);
    }
  };

  const closeInPlaceModal = () => {
    if (!reprocessLoading) {
      setInPlaceModal(null);
    }
  };

  const openInPlaceModal = useCallback((row) => {
    setInPlaceModal({
      document_id: row.document_id || row.id,
      filename: row.filename,
    });
  }, []);

  const executeInPlaceReprocess = async () => {
    if (!inPlaceModal?.document_id) return;
    const docId = inPlaceModal.document_id;
    const filename = inPlaceModal.filename;
    setReprocessLoading(true);
    try {
      const result = await runInPlaceReprocess(callApi, docId);
      const status = result?.status;
      setToast({
        open: true,
        message: inPlaceReprocessToastMessage(result),
        severity: status === 'QUEUED' ? 'success' : 'error',
      });
      setInPlaceModal(null);
      if (status === 'QUEUED') {
        if (onReprocessStarted) {
          await onReprocessStarted({
            document_id: docId,
            status,
            filename,
          });
        } else if (onRefresh) {
          await onRefresh();
        }
      }
    } catch (err) {
      console.error('In-place reprocess failed:', err);
      setToast({
        open: true,
        message: err?.message || 'Error al reprocesar documento',
        severity: 'error',
      });
    } finally {
      setReprocessLoading(false);
    }
  };

  const executeReprocess = async (targetDocType) => {
    if (!reprocessModal || !targetDocType) return;
    setReprocessLoading(true);
    try {
      const data = await callApi(REPROCESS_DOCUMENT_AS, {
        input: {
          document_id: reprocessModal.document_id,
          target_doc_type: targetDocType,
        },
      });
      const result = data?.reprocessDocumentAs;
      const status = result?.status;
      const tipo = formatDocumentTypeLabel(
        result?.redirect_target_doc_type || targetDocType
      );

      if (status === 'QUEUED') {
        setToast({
          open: true,
          message: `Reproceso iniciado como ${tipo}. El archivo aparecerá en esa categoría mientras se procesa.`,
          severity: 'success',
        });
      } else if (status === 'ALREADY_EXISTS') {
        setToast({
          open: true,
          message: result?.message || `Ya existe un documento con ese contenido como ${tipo}`,
          severity: 'info',
        });
      } else {
        setToast({
          open: true,
          message: result?.message || 'No se pudo iniciar el reproceso',
          severity: 'error',
        });
      }

      setReprocessModal(null);
      closeFichaModal();

      if (onReprocessStarted && (status === 'QUEUED' || status === 'ALREADY_EXISTS')) {
        await onReprocessStarted({
          targetDocType: result?.redirect_target_doc_type || targetDocType,
          redirectTargetDocumentId: result?.redirect_target_document_id,
          filename: reprocessModal.filename,
          status,
        });
      } else if (onRefresh) {
        await onRefresh();
      }
    } catch (err) {
      console.error('Reprocess failed:', err);
      setToast({
        open: true,
        message: err?.message || 'Error al reprocesar documento',
        severity: 'error',
      });
    } finally {
      setReprocessLoading(false);
    }
  };


  const isFileClickable = (row) => {
    //return docType === 'invoice' || docType === 'receipt' || docType === 'unclassified';
    return true;
  };

  // Determine which actions should be enabled based on status and document type
  const getActionsForRow = (row) => {
    const status = (row.status || '').toLowerCase();
    const canReprocess = isReprocessableDocument(row, { canEdit, reprocessDocTypes });
    const canReprocessInPlace = isInPlaceReprocessable(row, { canEdit });

    return {
      viewMore: true,
      fileSummary: status === 'process_completed' || status === 'validating',
      download: false,
      rename: false,
      reprocessAs: canReprocess,
      tryAgain: canReprocessInPlace,
      delete: false,
    };
  };

  

  // Handle File Summary action
  const handleFileSummary = async (row) => {
    setSummaryData({
      fileName: row.filename,
      summary: '',
      fullContent: '',
      loading: true,
      error: null
    });
    setIsSummaryModalOpen(true);
    try {
      const data = await callApi(buildDocumentByIdQuery(row.document_id));
      const apiData = data.getDocumentById;
      const bedrock = resolveBedrockMetadata(apiData);
      const summary = bedrock.summary || bedrock.overview || '';
      setSummaryData({
        fileName: row.filename,
        summary: summary.slice(0, 190),
        fullContent: summary,
        loading: false,
        error: null
      });
      setIsSummaryModalOpen(true)
    } catch (err) {
      console.error("Error fetching invoice:", err);
      setResults([])
    }
     
  };

  // Handle action callbacks with row context
  const handleAction = (action, row) => {

    switch (action) {
      case 'viewMore':
        if (isFileClickable(row)) {
          handleFileClick(row);
        } else if (onViewMore) {
          onViewMore(row);
        }
        break;
      case 'fileSummary':
        handleFileSummary(row);
        break;
      case 'download':
        onDownload && onDownload(row);
        break;
      case 'rename':
        onRename && onRename(row);
        break;
      case 'tryAgain':
        if (isInPlaceReprocessable(row, { canEdit })) {
          openInPlaceModal(row);
        } else if (onTryAgain) {
          onTryAgain(row);
        }
        break;
      case 'delete':
        onDelete && onDelete(row);
        break;
      default:
        break;
    }
  };

  // Get document type label for display
  const getDocumentTypeLabel = (row) => {
    const bedrock = resolveBedrockMetadata(row);
    if (bedrock && typeof bedrock === 'object' && bedrock.tipo_documento) {
      return formatComprobanteLabel(bedrock);
    }
    const docType = getDocumentType(row);
    if (docType === 'invoice') return 'Comprobante';
    if (docType === 'receipt') return 'Recibo';
    return docType || 'Sin clasificar';
  };

  // Calculate document counts
  const getDocumentCounts = () => {
    const counts = {
      all: data.length,
      invoices: 0,
      receipts: 0,
      unclassified: 0
    };

    data.forEach(doc => {
      const docType = getDocumentType(doc);
      if (docType === 'invoice') counts.invoices++;
      else if (docType === 'receipt') counts.receipts++;
      else counts.unclassified++;
    });

    return counts;
  };

  const counts = getDocumentCounts();
  const filteredData = getFilteredData();

  const money = (value, currency = 'ARS') => {
    if (value == null || value === '') return '—';
    const amount = Number(value);
    if (Number.isNaN(amount)) return '—';
    return amount.toLocaleString('es-AR', {
      style: 'currency',
      currency: currency || 'ARS',
      minimumFractionDigits: 2,
    });
  };

  const shortDate = (date) => {
    if (!date) return '—';
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return date;
    return parsed.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  const formatCuit = (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    if (digits.length !== 11) return value || '—';
    return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
  };

  const confidenceMeta = (band) => {
    if (band === 'high') return ['Alta', 'high'];
    if (band === 'medium') return ['Media', 'medium'];
    if (band === 'low') return ['Baja', 'low'];
    return ['—', 'unknown'];
  };

  const arcaMeta = (validation, row) => {
    const display = resolveArcaDisplay(validation, row, { enabled: arcaEnabled });
    if (!display) return null;
    return [display.label, display.tone, display];
  };

  const handleArcaConstatar = async (row) => {
    const docId = row.document_id || row.id;
    if (!docId || !canConstatar) return;
    setArcaLoadingId(docId);
    try {
      await constatarDocumentArca(docId);
      if (onRefresh) await onRefresh();
      setToast({ open: true, severity: 'success', message: 'Validación ARCA iniciada' });
    } catch (err) {
      setToast({
        open: true,
        severity: 'error',
        message: err?.message || 'No se pudo validar con ARCA',
      });
    } finally {
      setArcaLoadingId(null);
    }
  };

  const typeMeta = (docType, label) => {
    if (docType === 'invoice') return { label, color: '#0066cc' };
    if (docType === 'receipt') return { label: label || 'Recibo', color: '#7c3aed' };
    if (docType === 'unclassified') return { label: 'Sin clasificar', color: '#6b7280' };
    if (/cr[eé]dito/i.test(label || '')) return { label, color: '#059669' };
    if (/d[eé]bito/i.test(label || '')) return { label, color: '#dc2626' };
    return { label: label || 'Comprobante', color: '#0066cc' };
  };

  const rowFiscal = (row) => {
    const bedrock = resolveBedrockMetadata(row);
    const supplier = row.supplier_name || bedrock.razon_social_emisor || bedrock.titular_origen || 'Proveedor no identificado';
    const cuit = row.supplier_cuit || bedrock.cuit_emisor || bedrock.cuit_origen || '';
    const fullNumber = row.full_number
      || [bedrock.punto_de_venta, bedrock.numero_documento].filter(Boolean).join('-')
      || '—';
    const amount = row.total_amount ?? bedrock.monto_total ?? bedrock.importe_transferido;
    const currency = row.currency || bedrock.moneda || 'ARS';
    const issueDate = row.issue_date || bedrock.fecha_emision || bedrock.fecha_transferencia || row.end_at || row.updated_at;
    const [confidenceLabel, confidenceTone] = confidenceMeta(row.confidence_band);
    const typeLabel = getDocumentTypeLabel(row);
    return { supplier, cuit, fullNumber, amount, currency, issueDate, confidenceLabel, confidenceTone, typeLabel };
  };

  const sumByCurrency = filteredData.reduce((acc, row) => {
    const bedrock = resolveBedrockMetadata(row);
    const amount = Number(row.total_amount ?? bedrock.monto_total ?? bedrock.importe_transferido);
    if (!Number.isNaN(amount)) {
      const currency = row.currency || bedrock.moneda || 'ARS';
      acc[currency] = (acc[currency] || 0) + amount;
    }
    return acc;
  }, {});

  return (
    <>
      <div className="search-results-container">
        <h2 className="results-title">
          {filteredData.length} comprobantes
          {documentTypeFilter === 'all' && (
            <span className="document-counts">
              {counts.invoices} facturas · {counts.receipts} recibos · {counts.unclassified} sin clasificar
            </span>
          )}
        </h2>
        <div className="smart-results-summary">
          {Object.entries(sumByCurrency).length > 0
            ? Object.entries(sumByCurrency).map(([currency, total]) => (
              <span key={currency}>{money(total, currency)}</span>
            ))
            : <span>Sin importes para sumar</span>}
        </div>

        <div className="smart-results-list">
          {filteredData.map((row, index) => {
            const actions = getActionsForRow(row);
            const docType = getDocumentType(row);
            const docId = row.document_id || row.id;
            const isProcessingState = isProcessing(row.status) || isDocumentReprocessing(row);
            const processingLabel = isReprocessingStatus(row.status) || isDocumentReprocessing(row)
              ? 'Reprocesando'
              : getActiveProcessingLabel(row.status);
            const fiscal = rowFiscal(row);
            const statusLower = String(row.status || '').toLowerCase();
            const needsAction = docType === 'unclassified'
              || fiscal.confidenceTone === 'low'
              || statusLower.includes('failed')
              || statusLower.includes('error');
            const meta = typeMeta(docType, fiscal.typeLabel);
            const amountLabel = docType === 'unclassified'
              ? '— revisar'
              : money(fiscal.amount, fiscal.currency);
            const arca = arcaMeta(row.arca_validation, row);

            return (
              <article
                key={row.document_id || row.id || index}
                className={`result-row-card ${needsAction ? 'is-exception' : ''} ${isProcessingState ? 'is-processing' : ''}`}
              >
                <div className="rr-main">
                  <span className="rr-pill" style={{ background: `${meta.color}1a`, color: meta.color }}>
                    {meta.label}
                  </span>
                  <button type="button" className="rr-supplier" onClick={() => handleFileClick(row)} title={row.filename}>
                    {fiscal.supplier}
                  </button>
                  <span className={`rr-amount ${fiscal.currency === 'USD' ? 'usd' : ''}`}>
                    {amountLabel}
                  </span>
                </div>
                <div className="rr-meta">
                  <span className="mono-muted">{fiscal.fullNumber}</span>
                  <span className="rr-sep">·</span>
                  <span>{shortDate(fiscal.issueDate)}</span>
                  <span className="rr-sep">·</span>
                  <span className="mono-muted">CUIT {formatCuit(fiscal.cuit)}</span>
                  <span className={`rr-conf rr-conf-${fiscal.confidenceTone}`} title={`Confianza ${fiscal.confidenceLabel}`}>
                    <span />
                    {fiscal.confidenceTone === 'unknown' ? 'confianza —' : `confianza ${fiscal.confidenceLabel.toLowerCase()}`}
                  </span>
                  {row.cae === 'expired' && <span className="cae-badge">CAE vencido</span>}
                  {row.cae === 'invalid' && <span className="cae-badge">CAE inválido</span>}
                  {arca && <span className={`rr-arca rr-arca-${arca[1]}`}>{arca[0]}</span>}
                  {arca?.[2]?.showConstatar && canConstatar && (
                    <button
                      type="button"
                      className="rr-arca-action"
                      disabled={arcaLoadingId === (row.document_id || row.id)}
                      onClick={() => handleArcaConstatar(row)}
                    >
                      {arcaLoadingId === (row.document_id || row.id) ? 'Validando…' : 'Constatar ARCA'}
                    </button>
                  )}
                  {isProcessingState && (
                    <span className="rr-processing">
                      <Spinner size="small" />
                      {processingLabel}
                    </span>
                  )}
                  <span className="rr-actions">
                    <button type="button" className={needsAction ? 'btn-warn-row' : 'btn-primary-row'} onClick={() => handleFileClick(row)}>
                      {docType === 'unclassified' ? 'Clasificar' : needsAction ? 'Revisar' : 'Ver'}
                    </button>
                    {isInPlaceReprocessable(row, { canEdit }) && !isProcessingState && (
                      <button
                        type="button"
                        className="btn-secondary-row"
                        onClick={() => openInPlaceModal(row)}
                      >
                        Reprocesar
                      </button>
                    )}
                    <MoreActionsMenu
                      actions={actions}
                      position="bottom-right"
                      disabled={isProcessingState}
                      onMenuOpen={() => handleReprocessMenuOpen(row)}
                      onReprocessAs={() => openReprocessModal(row)}
                      onViewMore={() => handleAction('viewMore', row)}
                      onFileSummary={() => handleAction('fileSummary', row)}
                      onDownload={() => handleAction('download', row)}
                      onRename={() => handleAction('rename', row)}
                      onTryAgain={() => handleAction('tryAgain', row)}
                      onDelete={() => handleAction('delete', row)}
                      triggerButton={<span className="rr-kebab">⋮</span>}
                    />
                  </span>
                </div>
              </article>
            );
          })}

          {filteredData.length === 0 && (
            <div className="smart-empty-state">
              <h3>Sin resultados</h3>
              <p>No hay comprobantes que coincidan con estos filtros.</p>
            </div>
          )}
        </div>
      </div>

      {/* SPEC-12 Ficha comprobante */}
      <DocumentViewModal
        key={fichaModal.record?.document_id}
        isOpen={fichaModal.open}
        onClose={closeFichaModal}
        documentRecord={fichaModal.record}
        canEdit={canEdit}
        reprocessDocTypes={reprocessDocTypes}
        onClassify={handleFichaClassify}
      />

      <ReprocessDocumentModal
        isOpen={Boolean(reprocessModal)}
        filename={reprocessModal?.filename}
        docTypes={reprocessDocTypes}
        loading={reprocessLoading}
        onConfirm={executeReprocess}
        onCancel={closeReprocessModal}
      />

      <ReprocessInPlaceModal
        isOpen={Boolean(inPlaceModal)}
        filename={inPlaceModal?.filename}
        loading={reprocessLoading}
        onConfirm={executeInPlaceReprocess}
        onCancel={closeInPlaceModal}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Transfer Receipt Modal */}
      <TransferReceiptModal
        isOpen={isReceiptModalOpen}
        onClose={closeReceiptModal}
        transferData={selectedReceipt}
        onConciliate={handleConciliate}
        showConciliateButton={true}
      />

      {/* File Summary Modal */}
      <FileSummaryModal
        isOpen={isSummaryModalOpen}
        onClose={() => setIsSummaryModalOpen(false)}
        fileName={summaryData.fileName}
        summary={summaryData.summary}
        fullContent={summaryData.fullContent}
        loading={summaryData.loading}
        error={summaryData.error}
      />
    </>
  );
};

export default SearchResultsTable;